import {
  ChangeEventHandler,
  FocusEventHandler,
  PropsWithChildren,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from "react";

// Third parties
import { useDispatch, useSelector } from "react-redux";

import debug from "debug";

// Evergiving
import { FormConfig } from "@evergiving/common/dist/es2015/form";

// Form components
import { AddressField } from "@/lib/components/AddressField";
import { ContactConsentField } from "@/lib/components/ContactConsentField";
import { CountryField } from "@/lib/components/CountryField";
import { DateOfBirthAdultCheckField } from "@/lib/components/DateOfBirthAdultCheckField";
import { DateOfBirthField } from "@/lib/components/DateOfBirthField";
import { EmailAddressField } from "@/lib/components/EmailAddressField";
import { FormField } from "@/lib/components/FormField";
import { ItalianFiscalCodeField } from "@/lib/components/ItalianFiscalCodeField";
import { LabelledField } from "@/lib/components/LabelledField";
import { LabelText } from "@/lib/components/LabelText";
import { PaymentPageContainer } from "@/lib/components/PaymentPageContainer";
import { PhoneNumberField } from "@/lib/components/PhoneNumberField";
import { SingleSelectField } from "@/lib/components/SingleSelectField";
import { Text } from "@/lib/components/Text";

// Hooks
import { useSubmissionPrefetching } from "@/lib/hooks/useSubmissionPrefetching";
import { useAfterAddressFieldCommon } from "@/lib/hooks/useAfterAddressFieldCommon";
import { useNavigationWarning } from "@/lib/hooks/useNavigationWarning";

// Redux
import { setFormErrors } from "@/lib/redux/actions";
import { RootState } from "@/lib/redux/types";

export const DonorFields = ({
  formConfig,
  children: AfterAddressField,
}: PropsWithChildren<{
  formConfig: FormConfig;
}>): ReactElement => {
  const formRef = useRef<HTMLFormElement>(null);

  const {
    errors,
    freeFormAddressNowComplete,
    anyFormErrors,
  } = useAfterAddressFieldCommon({ formConfig });

  const dispatch = useDispatch();

  useSubmissionPrefetching(formConfig);

  useNavigationWarning(formConfig);

  const [
    freeFormAddressComplete,
    setFreeFormAddressComplete,
  ] = useState<boolean>(!!freeFormAddressNowComplete);

  useEffect(() => {
    if (freeFormAddressNowComplete && !freeFormAddressComplete) {
      setFreeFormAddressComplete(true);
    }
  }, [freeFormAddressComplete, freeFormAddressNowComplete]);

  const log = debug(__filename);

  const melissaAddressPresent = useSelector(
    (state: RootState): boolean => state.postalAddress?.kind === "Melissa"
  );

  const hasTitleComponent = formConfig.title !== undefined;

  // FIXME: just improving things for the F2F Congress lead form
  // TODO: probably refactor every other field to use onChange validation + debounce (see inside PhoneNumberField component)
  const handleBlur: FocusEventHandler<HTMLFormElement> = ({
    target: { name },
  }) => {
    if (name !== "phoneNumber" && anyFormErrors) {
      log(`DonatioDetailsPage.handleBlur errors: ${JSON.stringify(errors)}`);

      dispatch(setFormErrors(errors));
    }
  };

  const handleChange: ChangeEventHandler<HTMLFormElement> = ({
    target: { name },
  }) => {
    if (name === "phoneNumber" && anyFormErrors) {
      log(`DonatioDetailsPage.handleChange errors: ${JSON.stringify(errors)}`);

      dispatch(setFormErrors(errors));
    }
  };

  const contactConsentFieldConfig = formConfig.contactConsent;

  return (
    <PaymentPageContainer formConfig={formConfig}>
      <form
        id="other-payment-method"
        aria-label="Personal and Payment information"
        onBlur={handleBlur}
        onChange={handleChange}
        ref={formRef}
      >
        {/* eslint-disable jsx-a11y/no-autofocus */}
        {/* eslint-disable-next-line no-warning-comments */}
        {/* ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^ Discussion of this override follows:

             https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/master/docs/rules/no-autofocus.md
             says that "[a]utofocusing elements can cause usability issues for sighted
             and non-sighted users, alike."

             It links to https://www.brucelawson.co.uk/2009/the-accessibility-of-html-5-autofocus/,
             which, however, carves out an exception for contact forms like this one. Therefore, we keep
             autofocus and disable the eslint rule.

             TODO: we need to double-check when entering the form with a screen reader, the
             user knows the context as described in that second link.

             */}
        {hasTitleComponent ? (
          <SingleSelectField
            config={formConfig.title!}
            name="title"
            autofocus
          />
        ) : null}
        <FormField
          config={formConfig.firstName}
          name="firstName"
          htmlName="fname"
          autoComplete="billing given-name"
          autoCapitalize="words"
          autoFocus={!hasTitleComponent}
          spellCheck="false"
        />
        {/* eslint-enable jsx-a11y/no-autofocus */}
        <FormField
          config={formConfig.lastName}
          name="lastName"
          htmlName="lname"
          autoComplete="billing family-name"
          autoCapitalize="words"
          spellCheck="false"
        />
        {formConfig.emailAddress !== undefined ? (
          <EmailAddressField config={formConfig.emailAddress} />
        ) : null}
        {formConfig.dateOfBirth !== undefined &&
        !("checkboxText" in formConfig.dateOfBirth) ? (
          <DateOfBirthField
            config={formConfig.dateOfBirth}
            locale={formConfig.locale}
          />
        ) : null}
        {formConfig.dateOfBirth !== undefined &&
        "checkboxText" in formConfig.dateOfBirth ? (
          <DateOfBirthAdultCheckField config={formConfig.dateOfBirth} />
        ) : null}
        {formConfig.gender !== undefined && (
          <SingleSelectField config={formConfig.gender} name="gender" />
        )}
        {formConfig.personalIdentificationNumber !== undefined
          ? formConfig.locale === "it-IT" && (
              <ItalianFiscalCodeField
                config={formConfig.personalIdentificationNumber}
                name="belfioreCode"
              />
            )
          : null}
        {formConfig.profession !== undefined ? (
          <SingleSelectField config={formConfig.profession} name="profession" />
        ) : null}
        {formConfig.country ? (
          <LabelledField id="billingCountry">
            <LabelText requiredIndicator>
              <Text value={formConfig.country.labelText} />
            </LabelText>
            <CountryField countryFieldConfig={formConfig.country} />
          </LabelledField>
        ) : null}
        {formConfig.phoneNumber !== undefined ? (
          <PhoneNumberField
            config={formConfig.phoneNumber}
            htmlName="phone"
            autoComplete="tel"
            inputMode="tel"
          />
        ) : null}
        {formConfig.address !== undefined ? (
          <div aria-live="polite">
            <AddressField
              locale={formConfig.locale}
              countryFieldConfig={formConfig.country}
              addressFieldConfig={formConfig.address}
              defaultCountryCode={formConfig.defaultCountryCode}
            />
          </div>
        ) : null}
        {formConfig.address === undefined ||
        melissaAddressPresent ||
        freeFormAddressComplete ? (
          <>
            {contactConsentFieldConfig ? (
              <ContactConsentField
                formConfig={formConfig}
                contactConsentFieldConfig={contactConsentFieldConfig}
              />
            ) : null}
            {AfterAddressField}
          </>
        ) : null}
      </form>
    </PaymentPageContainer>
  );
};
